import { useState, useEffect, useContext, useCallback } from 'react';
import { Booking, ICalculateBookingDaysOptions, VehicleStatus } from '../logic/api';
import { SessionContext } from '../utility/useSession';
import { ReleaseFlag } from '../utility/feature-management';
import { useReleaseManagement } from '../utility/feature-management';
import { toZonedTime } from 'date-fns-tz';
import { differenceInCalendarDays, differenceInDays, subMilliseconds } from 'date-fns';

const calculateBookingDuration = (booking: Booking, options: ICalculateBookingDaysOptions) => {
  const { timeZone, useWalkInEndDate, useBookingEndDate, useCalendarDays } = options;
  let endsAt = toZonedTime(booking.expectedReturnDate, timeZone);
  const startsAt = toZonedTime(booking.bookingStartDate, timeZone);

  if (
    useBookingEndDate ||
    (useWalkInEndDate && booking.isWalkIn && booking.vehicle.statusId < VehicleStatus.CheckedIn)
  ) {
    endsAt = toZonedTime(booking.bookingEndDate, timeZone);
  }

  startsAt.setSeconds(0, 0);
  endsAt.setSeconds(0, 0);
  endsAt = subMilliseconds(endsAt, 1);

  const bookingDuration = useCalendarDays
    ? differenceInCalendarDays(endsAt, startsAt) + 1
    : differenceInDays(endsAt, startsAt) + 1;
  return Math.max(bookingDuration, 0);
}

export const useBookingDays = (booking: Booking) => {
  const session = useContext(SessionContext);
  const preferences = session.current.site?.preferences;
  const [bookingDays, setBookingDays] = useState<number | null>(null);

  const isFeatureEnabled = useReleaseManagement(ReleaseFlag.releaseCalculateBookingDays);

  const calculateDays = useCallback(async () => {
    if (!preferences?.time_zone) return;

    const duration = calculateBookingDuration(booking, {
      timeZone: preferences.time_zone,
      useCalendarDays: preferences.overstays_tolerance_midnight,
    });

    setBookingDays(duration);
  }, [
    booking,
    preferences?.time_zone,
    preferences?.overstays_tolerance_midnight
  ]);

  useEffect(() => {
    if (!isFeatureEnabled) {
      setBookingDays(null);
      return;
    };

    calculateDays();
  }, [calculateDays, isFeatureEnabled]);

  return bookingDays;
};